import { useState, useEffect, useCallback, useMemo } from 'react';
import type { FC } from 'react';
import {
  Drawer, Form, Input,
  message, Select, Space,
} from 'antd';
import {
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { keyNodeApi } from '../api/services';
import type {
  KeyNodeVO,
  KeyNodeSaveDTO,
  KeyNodeMappingVO,
  IpdCascadeVO,
  IpcCascadeVO,
} from '../api/types';
import TableSelect from '../components/TableSelect';
import type { TableSelectColumn } from '../components/TableSelect';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useListViewQuery } from '../hooks/useListViewQuery';
import { LIST_VIEW_KEYS } from '../utils/listViewSession';
import {
  IpcButton,
  IpcFilterToolbar,
  IpcModal,
  IpcRowActions,
  IpcSwitch,
  IpcTable,
} from '../components/ui';
import './MonitorNodeConfig.css';

type IpdWorkItemRow = {
  key: string;
  projectKey: string;
  projectName: string;
  workItemTypeKey: string;
  workItemTypeName: string;
};

type IpdTemplateRow = IpdWorkItemRow & {
  templateCode: string;
  templateId: string;
  templateName: string;
};

type IpdStateRow = IpdTemplateRow & {
  stateCode: string;
  stateKey: string;
  stateName: string;
};

type IpcTemplateRow = {
  key: string;
  templateCode: string;
  templateId: number;
  templateName: string;
};

type IpcNodeRow = IpcTemplateRow & {
  nodeCode: string;
  nodeId: number;
  nodeName: string;
};

const makeIpdTemplateKey = (workItemTypeKey: string, templateId: string) => `${workItemTypeKey}|${templateId}`;
const makeIpdStateKey = (workItemTypeKey: string, templateId: string, stateKey: string) => `${workItemTypeKey}|${templateId}|${stateKey}`;
const makeIpcNodeKey = (templateId: string | number, nodeId: string | number) => `${templateId}|${nodeId}`;

type ViewMappingRow = {
  key: string;
  keyNodeLabel: string;
  ipdWorkItemTypeName: string;
  ipdTemplateName: string;
  ipdStateName: string;
  ipcTemplateName: string;
  ipcNodeName: string;
};

const emptyViewMappingFilter = {
  keyNode: '',
  ipdWorkItem: '',
  ipdTemplate: '',
  ipdNode: '',
  ipcTemplate: '',
  ipcNode: '',
};

function buildViewMappingRows(node: KeyNodeVO, mappings: KeyNodeMappingVO[]): ViewMappingRow[] {
  const keyNodeLabel = `${node.nodeCode} ${node.name}`;
  const ipdMaps = mappings.filter(m => m.mappingType === 'ipd');
  const ipcMaps = mappings.filter(m => m.mappingType === 'ipc');

  if (ipdMaps.length === 0 && ipcMaps.length === 0) return [];

  const toRow = (
    key: string,
    ipd?: KeyNodeMappingVO,
    ipc?: KeyNodeMappingVO,
  ): ViewMappingRow => ({
    key,
    keyNodeLabel,
    ipdWorkItemTypeName: ipd
      ? (ipd.ipdWorkItemTypeName || ipd.ipdWorkItemTypeKey || '—')
      : '—',
    ipdTemplateName: ipd
      ? (ipd.ipdTemplateName || ipd.ipdTemplateId || '—')
      : '—',
    ipdStateName: ipd
      ? (ipd.ipdStateName || ipd.ipdStateKey || '—')
      : '—',
    ipcTemplateName: ipc
      ? (ipc.ipcTemplateName || (ipc.ipcTemplateId != null ? String(ipc.ipcTemplateId) : '—'))
      : '—',
    ipcNodeName: ipc
      ? (ipc.ipcNodeName || (ipc.ipcNodeId != null ? String(ipc.ipcNodeId) : '—'))
      : '—',
  });

  if (ipdMaps.length > 0 && ipcMaps.length > 0) {
    const rows: ViewMappingRow[] = [];
    let idx = 0;
    for (const ipd of ipdMaps) {
      for (const ipc of ipcMaps) {
        rows.push(toRow(`pair-${idx++}`, ipd, ipc));
      }
    }
    return rows;
  }

  if (ipdMaps.length > 0) {
    return ipdMaps.map((ipd, idx) => toRow(`ipd-${idx}`, ipd, undefined));
  }

  return ipcMaps.map((ipc, idx) => toRow(`ipc-${idx}`, undefined, ipc));
}

function matchViewMappingFilter(row: ViewMappingRow, filter: typeof emptyViewMappingFilter) {
  const includes = (value: string, keyword: string) => {
    if (!keyword.trim()) return true;
    return value.toLowerCase().includes(keyword.trim().toLowerCase());
  };

  return includes(row.keyNodeLabel, filter.keyNode)
    && includes(row.ipdWorkItemTypeName, filter.ipdWorkItem)
    && includes(row.ipdTemplateName, filter.ipdTemplate)
    && includes(row.ipdStateName, filter.ipdNode)
    && includes(row.ipcTemplateName, filter.ipcTemplate)
    && includes(row.ipcNodeName, filter.ipcNode);
}

const viewMappingColumns: ColumnsType<ViewMappingRow> = [
  { title: '关键节点', dataIndex: 'keyNodeLabel', key: 'keyNodeLabel', width: 160 },
  { title: 'IPD工作项', dataIndex: 'ipdWorkItemTypeName', key: 'ipdWorkItemTypeName', width: 130 },
  { title: 'IPD模板名称', dataIndex: 'ipdTemplateName', key: 'ipdTemplateName', width: 150 },
  { title: 'IPD节点名称', dataIndex: 'ipdStateName', key: 'ipdStateName', width: 150 },
  { title: 'IPC模板名称', dataIndex: 'ipcTemplateName', key: 'ipcTemplateName', width: 180 },
  { title: 'IPC节点名称', dataIndex: 'ipcNodeName', key: 'ipcNodeName', width: 150 },
];

const MonitorNodeConfig: FC = () => {
  const [allNodes, setAllNodes] = useState<KeyNodeVO[]>([]);
  const [loading, setLoading] = useState(false);
  // 分页
  const [total, setTotal] = useState(0);

  type ListFilters = { keyword: string; mappingFilter: string };
  const DEFAULT_LIST_FILTERS: ListFilters = { keyword: '', mappingFilter: 'all' };
  const {
    draft: listDraft,
    patchDraft: patchListDraft,
    applied: listApplied,
    applyIfChanged: applyListIfChanged,
    patchAndApply: patchAndApplyList,
    reset: resetListQuery,
    patchApplied: patchListApplied,
    page: pageNum,
    pageSize,
    setPagination: setListPagination,
  } = useListViewQuery<ListFilters>({
    listViewKey: LIST_VIEW_KEYS.monitorNodeConfig,
    defaultFilters: DEFAULT_LIST_FILTERS,
    emptyFilters: DEFAULT_LIST_FILTERS,
  });
  const draftKeyword = listDraft.keyword;
  const draftMappingFilter = listDraft.mappingFilter;
  const appliedKeyword = listApplied.keyword;
  const appliedMappingFilter = listApplied.mappingFilter;

  // 抽屉
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingNode, setEditingNode] = useState<KeyNodeVO | null>(null);
  const [form] = Form.useForm();
  const [alertEnabled, setAlertEnabled] = useState(true);

  // 映射抽屉
  const [mappingDrawerOpen, setMappingDrawerOpen] = useState(false);
  const [mappingNodeId, setMappingNodeId] = useState<number | null>(null);
  const [mappingNodeName, setMappingNodeName] = useState('');

  // IPD 独立多选（原型：项目空间 → 工作项 → 模板 → 节点，当前后端3级）
  const [ipdWorkItemTypes, setIpdWorkItemTypes] = useState<string[]>([]);
  const [ipdTemplates, setIpdTemplates] = useState<string[]>([]);
  const [ipdStates, setIpdStates] = useState<string[]>([]);

  // IPC 独立多选（模板 → 节点）
  const [ipcTemplates, setIpcTemplates] = useState<string[]>([]);
  const [ipcNodes, setIpcNodes] = useState<string[]>([]);

  // 查看映射弹窗
  const [viewMappingOpen, setViewMappingOpen] = useState(false);
  const [viewMappingNode, setViewMappingNode] = useState<KeyNodeVO | null>(null);
  const [viewMappings, setViewMappings] = useState<KeyNodeMappingVO[]>([]);
  const [viewMappingRows, setViewMappingRows] = useState<ViewMappingRow[]>([]);
  const [viewMappingsLoading, setViewMappingsLoading] = useState(false);
  const [viewMappingFilter, setViewMappingFilter] = useState(emptyViewMappingFilter);
  const [viewMappingFilterApplied, setViewMappingFilterApplied] = useState(emptyViewMappingFilter);

  // 删除确认
  const [deleteTarget, setDeleteTarget] = useState<KeyNodeVO | null>(null);
  const [ipdCascade, setIpdCascade] = useState<IpdCascadeVO[]>([]);
  const [ipcCascade, setIpcCascade] = useState<IpcCascadeVO[]>([]);

  const loadNodes = useCallback(async (p?: {
    pageNum?: number;
    pageSize?: number;
    keyword?: string;
    mappingFilter?: string;
  }) => {
    const kw = p?.keyword ?? appliedKeyword;
    const mf = p?.mappingFilter ?? appliedMappingFilter;
    const pn = p?.pageNum ?? pageNum;
    const ps = p?.pageSize ?? pageSize;
    setLoading(true);
    try {
      const params: {
        keyword?: string;
        mappingStatus?: string;
        pageNum?: number;
        pageSize?: number;
      } = {
        pageNum: pn,
        pageSize: ps,
      };
      if (kw) params.keyword = kw;
      if (mf === 'configured') params.mappingStatus = 'configured';
      else if (mf === 'unconfigured') params.mappingStatus = 'unconfigured';

      const res = await keyNodeApi.getList(params);
      setAllNodes(res.list ?? []);
      setTotal(res.total ?? 0);
    } catch {
      message.error('加载关键节点失败');
    } finally {
      setLoading(false);
    }
  }, [appliedKeyword, appliedMappingFilter, pageNum, pageSize]);

  useEffect(() => {
    void loadNodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 首屏
  }, []);

  const loadNodesIfApplied = (coords: {
    applied: ListFilters;
    page: number;
    pageSize: number;
  } | null) => {
    if (!coords) return;
    void loadNodes({
      pageNum: coords.page,
      pageSize: coords.pageSize,
      keyword: coords.applied.keyword,
      mappingFilter: coords.applied.mappingFilter,
    });
  };

  const handleSearch = () => {
    loadNodesIfApplied(applyListIfChanged({
      keyword: listDraft.keyword.trim(),
      mappingFilter: listDraft.mappingFilter,
    }));
  };

  /** 重置 / 清除全部：默认即空关键词 + 全部映射 */
  const clearAllListFilters = () => {
    loadNodesIfApplied(resetListQuery());
  };
  const resetListFiltersToDefault = clearAllListFilters;

  const listAppliedChips = [
    appliedKeyword.trim() ? { key: 'keyword', label: `关键词: ${appliedKeyword.trim()}` } : null,
    appliedMappingFilter === 'configured'
      ? { key: 'mapping', label: '映射: 已配置' }
      : appliedMappingFilter === 'unconfigured'
        ? { key: 'mapping', label: '映射: 待配置' }
        : null,
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeListAppliedChip = (key: string) => {
    if (key === 'keyword') {
      loadNodesIfApplied(patchListApplied({ keyword: '' }));
      return;
    }
    if (key === 'mapping') {
      loadNodesIfApplied(patchListApplied({ mappingFilter: 'all' }));
    }
  };

  const viewMappingAppliedChips = [
    viewMappingFilterApplied.keyNode
      ? { key: 'keyNode', label: `关键节点: ${viewMappingFilterApplied.keyNode}` }
      : null,
    viewMappingFilterApplied.ipdWorkItem
      ? { key: 'ipdWorkItem', label: `IPD 工作项: ${viewMappingFilterApplied.ipdWorkItem}` }
      : null,
    viewMappingFilterApplied.ipdTemplate
      ? { key: 'ipdTemplate', label: `IPD 模板: ${viewMappingFilterApplied.ipdTemplate}` }
      : null,
    viewMappingFilterApplied.ipdNode
      ? { key: 'ipdNode', label: `IPD 节点: ${viewMappingFilterApplied.ipdNode}` }
      : null,
    viewMappingFilterApplied.ipcTemplate
      ? { key: 'ipcTemplate', label: `IPC 模板: ${viewMappingFilterApplied.ipcTemplate}` }
      : null,
    viewMappingFilterApplied.ipcNode
      ? { key: 'ipcNode', label: `IPC 节点: ${viewMappingFilterApplied.ipcNode}` }
      : null,
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeViewMappingAppliedChip = (key: string) => {
    const next = { ...viewMappingFilterApplied, [key]: '' };
    setViewMappingFilter(prev => ({ ...prev, [key]: '' }));
    setViewMappingFilterApplied(next);
  };

  // 打开创建
  const handleOpenCreate = () => {
    setEditingNode(null);
    setAlertEnabled(true);
    form.resetFields();
    form.setFieldsValue({
      alertEnabled: true,
      alertDays: 3,
    });
    setDrawerOpen(true);
  };

  // 打开编辑
  const handleOpenEdit = (record: KeyNodeVO) => {
    setEditingNode(record);
    setAlertEnabled(record.alertEnabled);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      alertEnabled: record.alertEnabled,
      alertDays: Number(record.alertDays) || 3,
    });
    setDrawerOpen(true);
  };

  // 保存
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (values.alertEnabled && (!values.alertDays || values.alertDays < 1)) {
        message.error('请输入有效的预警天数');
        return;
      }
      const dto: KeyNodeSaveDTO = {
        id: editingNode?.id,
        name: values.name,
        description: values.description,
        monitorEnabled: 1,
        alertEnabled: values.alertEnabled ? 1 : 0,
        alertDays: values.alertDays != null ? Number(values.alertDays) : 3,
        sortOrder: editingNode?.sortOrder ?? 0,
      };

      try {
        if (editingNode) {
          await keyNodeApi.update(editingNode.id, dto);
        } else {
          await keyNodeApi.create(dto);
        }
      } catch {
        message.error('保存失败');
        return;
      }

      message.success('关键节点已保存');
      setDrawerOpen(false);
      if (editingNode) {
        void loadNodes();
      } else {
        const lastPage = Math.max(1, Math.ceil((total + 1) / pageSize));
        const coords = setListPagination(lastPage, pageSize);
        void loadNodes({ pageNum: coords.page, pageSize: coords.pageSize });
      }
    } catch {
      // validation error
    }
  };

  const handleToggleAlert = async (id: number) => {
    const current = allNodes.find(n => n.id === id);
    if (!current) {
      message.error('节点未找到');
      return;
    }
    const newValue = !current.alertEnabled;
    try {
      await keyNodeApi.toggle(id, 'alert_enabled', newValue ? 1 : 0);
      setAllNodes(prev => prev.map(n => n.id === id ? { ...n, alertEnabled: newValue } : n));
    } catch {
      message.error('切换失败');
    }
  };

  // 删除
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await keyNodeApi.delete(deleteTarget.id);
      message.success('节点已删除');
      setDeleteTarget(null);
      loadNodes();
    } catch {
      message.error('删除失败');
    }
  };

  // 打开映射抽屉 — 回填已有配置
  const handleOpenMappings = async (record: KeyNodeVO) => {
    setMappingNodeId(record.id);
    setMappingNodeName(record.name);
    setIpdWorkItemTypes([]);
    setIpdTemplates([]);
    setIpdStates([]);
    setIpcTemplates([]);
    setIpcNodes([]);
    setMappingDrawerOpen(true);

    // 异步加载已有映射数据回填
    try {
      const existing = await keyNodeApi.getMappings(record.id);
      if (existing && existing.length > 0) {
        const ipdWits = new Set<string>();
        const ipdTpls = new Set<string>();
        const ipdSts = new Set<string>();
        const ipcTpls = new Set<string>();
        const ipcNds = new Set<string>();
        existing.forEach(m => {
          if (m.mappingType === 'ipd') {
            if (m.ipdWorkItemTypeKey) ipdWits.add(m.ipdWorkItemTypeKey);
            if (m.ipdWorkItemTypeKey && m.ipdTemplateId) {
              ipdTpls.add(makeIpdTemplateKey(m.ipdWorkItemTypeKey, m.ipdTemplateId));
            }
            if (m.ipdWorkItemTypeKey && m.ipdTemplateId && m.ipdStateKey) {
              ipdSts.add(makeIpdStateKey(m.ipdWorkItemTypeKey, m.ipdTemplateId, m.ipdStateKey));
            }
          } else if (m.mappingType === 'ipc') {
            if (m.ipcTemplateId != null) ipcTpls.add(String(m.ipcTemplateId));
            if (m.ipcTemplateId != null && m.ipcNodeId != null) {
              ipcNds.add(makeIpcNodeKey(m.ipcTemplateId, m.ipcNodeId));
            }
          }
        });
        setIpdWorkItemTypes(Array.from(ipdWits));
        setIpdTemplates(Array.from(ipdTpls));
        setIpdStates(Array.from(ipdSts));
        setIpcTemplates(Array.from(ipcTpls));
        setIpcNodes(Array.from(ipcNds));
      }
    } catch {
      // 加载失败不影响打开抽屉
    }
  };

  // 保存映射
  const handleSaveMappings = async () => {
    if (mappingNodeId == null) {
      message.error('节点ID丢失，请重新打开映射配置');
      return;
    }

    const hasIpd = ipdWorkItemTypes.length > 0 && ipdTemplates.length > 0 && ipdStates.length > 0;
    const hasIpc = ipcTemplates.length > 0 && ipcNodes.length > 0;
    if (!hasIpd && !hasIpc) {
      message.error('请至少配置一组完整的映射关系');
      return;
    }

    const ipdMappings: Array<{ mappingType: 'ipd'; ipdWorkItemTypeKey: string; ipdTemplateId: string; ipdStateKey: string }> = [];
    if (hasIpd) {
      for (const wit of ipdWorkItemTypes) {
        const templateKeys = ipdTemplates.filter(key => key.startsWith(`${wit}|`));
        for (const templateKey of templateKeys) {
          const templateId = templateKey.split('|')[1];
          const stateKeys = ipdStates.filter(key => key.startsWith(`${templateKey}|`));
          for (const stateKey of stateKeys) {
            const state = stateKey.split('|')[2];
            ipdMappings.push({
              mappingType: 'ipd',
              ipdWorkItemTypeKey: wit,
              ipdTemplateId: templateId,
              ipdStateKey: state,
            });
          }
        }
      }
    }

    const ipcMappings: Array<{ mappingType: 'ipc'; ipcTemplateId: number; ipcNodeId: number }> = [];
    if (hasIpc) {
      for (const tpl of ipcTemplates) {
        const nodeKeys = ipcNodes.filter(key => key.startsWith(`${tpl}|`));
        for (const nodeKey of nodeKeys) {
          ipcMappings.push({
            mappingType: 'ipc',
            ipcTemplateId: Number(tpl),
            ipcNodeId: Number(nodeKey.split('|')[1]),
          });
        }
      }
    }

    if (ipdMappings.length === 0 && ipcMappings.length === 0) {
      message.error('请至少配置一组完整且层级匹配的映射关系');
      return;
    }

    try {
      await keyNodeApi.saveMappings(mappingNodeId, {
        keyNodeId: mappingNodeId,
        mappings: [...ipdMappings, ...ipcMappings],
      });
    } catch (err: any) {
      console.error('映射保存失败', err);
      message.error(err?.message || '映射保存失败');
      return;
    }
    message.success('映射保存成功');
    setMappingDrawerOpen(false);
    loadNodes();
  };

  // 查看映射弹窗 — 笛卡尔积全联匹配视图
  const handleViewMappings = async (record: KeyNodeVO) => {
    setViewMappingNode(record);
    setViewMappingFilter(emptyViewMappingFilter);
    setViewMappingFilterApplied(emptyViewMappingFilter);
    setViewMappingOpen(true);
    setViewMappingsLoading(true);
    try {
      const data = await keyNodeApi.getMappings(record.id);
      const items = data ?? [];

      // Name 填充
      items.forEach(m => {
        if (m.mappingType === 'ipd') {
          const wt = ipdCascade.find(c => c.workItemTypeKey === m.ipdWorkItemTypeKey);
          if (wt) {
            m.ipdWorkItemTypeName = m.ipdWorkItemTypeName || wt.workItemTypeName;
            const tpl = wt.templates.find(t => t.templateId === m.ipdTemplateId);
            if (tpl) {
              m.ipdTemplateName = m.ipdTemplateName || tpl.templateName;
              const st = tpl.states.find(s => s.stateKey === m.ipdStateKey);
              if (st) m.ipdStateName = m.ipdStateName || st.stateName;
            }
          }
        } else if (m.mappingType === 'ipc') {
          const tpl = ipcCascade.find(c => c.templateId === m.ipcTemplateId);
          if (tpl) {
            m.ipcTemplateName = m.ipcTemplateName || tpl.templateName;
            const node = tpl.nodes.find(n => n.nodeId === m.ipcNodeId);
            if (node) m.ipcNodeName = m.ipcNodeName || node.nodeName;
          }
        }
      });

      setViewMappings(items);
      setViewMappingRows(buildViewMappingRows(record, items));
    } catch {
      setViewMappings([]);
      setViewMappingRows([]);
    } finally {
      setViewMappingsLoading(false);
    }
  };

  const filteredViewMappingRows = useMemo(
    () => viewMappingRows.filter(row => matchViewMappingFilter(row, viewMappingFilterApplied)),
    [viewMappingRows, viewMappingFilterApplied],
  );

  const handleViewMappingSearch = () => {
    const next = { ...viewMappingFilter };
    if (JSON.stringify(next) === JSON.stringify(viewMappingFilterApplied)) return;
    setViewMappingFilterApplied(next);
  };

  const clearAllViewMappingFilters = () => {
    setViewMappingFilter(emptyViewMappingFilter);
    setViewMappingFilterApplied(emptyViewMappingFilter);
  };
  const resetViewMappingFiltersToDefault = clearAllViewMappingFilters;

  const viewMappingColumnsResizable = useResizableColumns(viewMappingColumns);

  // 表格列
  const baseColumns: ColumnsType<KeyNodeVO> = useMemo(() => [
    {
      title: '序号', key: 'index', width: 64, align: 'center',
      render: (_: unknown, __: unknown, idx: number) => (pageNum - 1) * pageSize + idx + 1,
    },
    {
      title: '节点编码', dataIndex: 'nodeCode', key: 'nodeCode', width: 100,
      render: (v: string) => <span className="mnc-cell-code">{v}</span>,
    },
    {
      title: '节点名称', dataIndex: 'name', key: 'name', width: 140,
      render: (v: string) => <span className="mnc-cell-name">{v}</span>,
    },
    {
      title: '节点说明', dataIndex: 'description', key: 'description', width: 220, ellipsis: true,
      render: (v: string) => <span className="mnc-cell-desc">{v || '—'}</span>,
    },
    {
      title: '预警状态', dataIndex: 'alertEnabled', key: 'alertEnabled', width: 110,
      render: (v: boolean, record: KeyNodeVO) => (
        <IpcSwitch
          size="small"
          checked={v}
          onChange={() => handleToggleAlert(record.id)}
        />
      ),
    },
    {
      title: '预警天数', dataIndex: 'alertDays', key: 'alertDays', width: 90,
      render: (v: number) => <span className="mnc-cell-days">{v ?? 0}天</span>,
    },
    {
      title: '映射关系', dataIndex: 'mappingConfigured', key: 'mappingConfigured', width: 100, align: 'center',
      render: (v: boolean) => (
        <span className={v ? 'mnc-mapping-badge mnc-mapping-badge--configured' : 'mnc-mapping-badge mnc-mapping-badge--pending'}>
          {v ? '已配置' : '待配置'}
        </span>
      ),
    },
    {
      title: '操作', key: 'actions', width: 150, align: 'left', fixed: 'right' as const,
      render: (_: unknown, record: KeyNodeVO) => (
        <IpcRowActions
          maxVisible={4}
          actions={[
            { key: 'mapping', label: '映射', onClick: () => handleOpenMappings(record) },
            { key: 'view', label: '查看', onClick: () => handleViewMappings(record) },
            { key: 'edit', label: '编辑', onClick: () => handleOpenEdit(record) },
            { key: 'delete', label: '删除', danger: true, onClick: () => setDeleteTarget(record) },
          ]}
        />
      ),
    },
  ], [pageNum, pageSize, handleToggleAlert, handleOpenMappings, handleViewMappings, handleOpenEdit]);

  const keyNodeListTable = useResizableColumns(baseColumns);

  const loadCascadeData = useCallback(async () => {
    try {
      const [ipd, ipc] = await Promise.all([
        keyNodeApi.getCascadeIpd(),
        keyNodeApi.getCascadeIpc(),
      ]);
      setIpdCascade(ipd ?? []);
      setIpcCascade(ipc ?? []);
    } catch {
      message.error('加载映射选项失败');
    }
  }, []);

  useEffect(() => { loadCascadeData(); }, [loadCascadeData]);

  const ipdWorkItemRows = useMemo<IpdWorkItemRow[]>(() => ipdCascade.map(wt => ({
    key: wt.workItemTypeKey,
    projectKey: wt.projectKey || '—',
    projectName: wt.projectName || wt.projectKey || '—',
    workItemTypeKey: wt.workItemTypeKey,
    workItemTypeName: wt.workItemTypeName,
  })), [ipdCascade]);

  const ipdTemplateRows = useMemo<IpdTemplateRow[]>(() => {
    const rows: IpdTemplateRow[] = [];
    ipdCascade.forEach(wt => {
      if (ipdWorkItemTypes.length > 0 && !ipdWorkItemTypes.includes(wt.workItemTypeKey)) return;
      wt.templates.forEach(tpl => {
        rows.push({
          key: makeIpdTemplateKey(wt.workItemTypeKey, tpl.templateId),
          projectKey: tpl.projectKey || wt.projectKey || '—',
          projectName: tpl.projectName || wt.projectName || '—',
          workItemTypeKey: wt.workItemTypeKey,
          workItemTypeName: wt.workItemTypeName,
          templateCode: tpl.templateCode || tpl.templateId,
          templateId: tpl.templateId,
          templateName: tpl.templateName,
        });
      });
    });
    return rows;
  }, [ipdCascade, ipdWorkItemTypes]);

  const ipdStateRows = useMemo<IpdStateRow[]>(() => {
    const rows: IpdStateRow[] = [];
    ipdCascade.forEach(wt => {
      if (ipdWorkItemTypes.length > 0 && !ipdWorkItemTypes.includes(wt.workItemTypeKey)) return;
      wt.templates.forEach(tpl => {
        const templateKey = makeIpdTemplateKey(wt.workItemTypeKey, tpl.templateId);
        if (ipdTemplates.length > 0 && !ipdTemplates.includes(templateKey)) return;
        tpl.states.forEach(st => {
          rows.push({
            key: makeIpdStateKey(wt.workItemTypeKey, tpl.templateId, st.stateKey),
            projectKey: st.projectKey || tpl.projectKey || wt.projectKey || '—',
            projectName: st.projectName || tpl.projectName || wt.projectName || '—',
            workItemTypeKey: wt.workItemTypeKey,
            workItemTypeName: wt.workItemTypeName,
            templateCode: st.templateCode || tpl.templateCode || tpl.templateId,
            templateId: tpl.templateId,
            templateName: tpl.templateName,
            stateCode: st.stateCode || st.stateKey,
            stateKey: st.stateKey,
            stateName: st.stateName,
          });
        });
      });
    });
    return rows;
  }, [ipdCascade, ipdWorkItemTypes, ipdTemplates]);

  const ipcTemplateRows = useMemo<IpcTemplateRow[]>(() => ipcCascade.map(tpl => ({
    key: String(tpl.templateId),
    templateCode: tpl.templateCode || String(tpl.templateId),
    templateId: tpl.templateId,
    templateName: tpl.templateName,
  })), [ipcCascade]);

  const ipcNodeRows = useMemo<IpcNodeRow[]>(() => {
    const rows: IpcNodeRow[] = [];
    ipcCascade.forEach(tpl => {
      if (ipcTemplates.length > 0 && !ipcTemplates.includes(String(tpl.templateId))) return;
      tpl.nodes.forEach(node => {
        rows.push({
          key: makeIpcNodeKey(tpl.templateId, node.nodeId),
          templateCode: node.templateCode || tpl.templateCode || String(tpl.templateId),
          templateId: tpl.templateId,
          templateName: tpl.templateName,
          nodeCode: node.nodeCode || String(node.nodeId),
          nodeId: node.nodeId,
          nodeName: node.nodeName,
        });
      });
    });
    return rows;
  }, [ipcCascade, ipcTemplates]);

  const handleIpdWorkItemChange = (next: string[]) => {
    setIpdWorkItemTypes(next);
    setIpdTemplates(prev => prev.filter(key => next.includes(key.split('|')[0])));
    setIpdStates(prev => prev.filter(key => next.includes(key.split('|')[0])));
  };

  const handleIpdTemplateChange = (next: string[]) => {
    setIpdTemplates(next);
    setIpdStates(prev => prev.filter(key => next.some(templateKey => key.startsWith(`${templateKey}|`))));
  };

  const handleIpcTemplateChange = (next: string[]) => {
    setIpcTemplates(next);
    setIpcNodes(prev => prev.filter(key => next.includes(key.split('|')[0])));
  };

  const ipdWorkItemColumns: TableSelectColumn<IpdWorkItemRow>[] = [
    { key: 'projectKey', title: '空间编码', width: 120, render: row => row.projectKey, searchText: row => row.projectKey },
    { key: 'workItemTypeKey', title: '工作项编码', width: 160, render: row => row.workItemTypeKey, searchText: row => row.workItemTypeKey },
    { key: 'workItemTypeName', title: '工作项名称', width: 160, render: row => row.workItemTypeName, searchText: row => row.workItemTypeName },
  ];

  const ipdTemplateColumns: TableSelectColumn<IpdTemplateRow>[] = [
    { key: 'projectKey', title: '空间编码', width: 100, render: row => row.projectKey, searchText: row => row.projectKey },
    { key: 'workItemTypeKey', title: '工作项编码', width: 140, render: row => row.workItemTypeKey, searchText: row => row.workItemTypeKey },
    { key: 'workItemTypeName', title: '工作项名称', width: 120, render: row => row.workItemTypeName, searchText: row => row.workItemTypeName },
    { key: 'templateCode', title: '模板编码', width: 100, render: row => row.templateCode, searchText: row => row.templateCode },
    { key: 'templateName', title: '模板名称', width: 140, render: row => row.templateName, searchText: row => row.templateName },
  ];

  const ipdStateColumns: TableSelectColumn<IpdStateRow>[] = [
    { key: 'projectKey', title: '空间编码', width: 90, render: row => row.projectKey, searchText: row => row.projectKey },
    { key: 'workItemTypeKey', title: '工作项编码', width: 130, render: row => row.workItemTypeKey, searchText: row => row.workItemTypeKey },
    { key: 'workItemTypeName', title: '工作项名称', width: 110, render: row => row.workItemTypeName, searchText: row => row.workItemTypeName },
    { key: 'templateCode', title: '模板编码', width: 90, render: row => row.templateCode, searchText: row => row.templateCode },
    { key: 'templateName', title: '模板名称', width: 110, render: row => row.templateName, searchText: row => row.templateName },
    { key: 'stateCode', title: '节点编码', width: 90, render: row => row.stateCode, searchText: row => row.stateCode },
    { key: 'stateName', title: '节点名称', width: 110, render: row => row.stateName, searchText: row => row.stateName },
  ];

  const ipcTemplateColumns: TableSelectColumn<IpcTemplateRow>[] = [
    { key: 'templateCode', title: '模板编码', width: 120, render: row => row.templateCode, searchText: row => row.templateCode },
    { key: 'templateName', title: '模板名称', width: 220, render: row => row.templateName, searchText: row => row.templateName },
  ];

  const ipcNodeColumns: TableSelectColumn<IpcNodeRow>[] = [
    { key: 'templateCode', title: '模板编码', width: 110, render: row => row.templateCode, searchText: row => row.templateCode },
    { key: 'templateName', title: '模板名称', width: 150, render: row => row.templateName, searchText: row => row.templateName },
    { key: 'nodeCode', title: '节点编码', width: 110, render: row => row.nodeCode, searchText: row => row.nodeCode },
    { key: 'nodeName', title: '节点名称', width: 150, render: row => row.nodeName, searchText: row => row.nodeName },
  ];

  return (
    <div className="monitor-node-config">
      <IpcFilterToolbar
        className="mnc-filter-bar"
        onReset={resetListFiltersToDefault}
        onSubmit={handleSearch}
        chipItems={listAppliedChips}
        onRemoveChip={removeListAppliedChip}
        onClearChips={clearAllListFilters}
        extra={
          <IpcButton variant="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            添加
          </IpcButton>
        }
      >
        <Input
          placeholder="节点编码 / 名称"
          value={draftKeyword}
          onChange={e => patchListDraft({ keyword: e.target.value })}
          allowClear
          onPressEnter={handleSearch}
          className="ipc-filter-bar__search"
        />
        <Select
          value={draftMappingFilter}
          onChange={v => loadNodesIfApplied(patchAndApplyList({ mappingFilter: v }))}
          placeholder="全部映射"
          options={[
            { value: 'all', label: '全部映射' },
            { value: 'configured', label: '已配置' },
            { value: 'unconfigured', label: '待配置' },
          ]}
          className="ipc-filter-bar__field"
        />
      </IpcFilterToolbar>

      <IpcTable<KeyNodeVO>
        listViewKey={LIST_VIEW_KEYS.monitorNodeConfig}
        panelClassName="mnc-table-region"
        scrollClassName="mnc-table-wrap"
        rowKey="id"
        columns={keyNodeListTable.columns}
        components={keyNodeListTable.components}
        tableLayout={keyNodeListTable.tableLayout}
        dataSource={allNodes}
        loading={loading}
        size="small"
        className={`mnc-table ${keyNodeListTable.tableClassName}`}
        autoHeight
        autoHeightDeps={[allNodes.length, pageNum, pageSize, loading]}
        scroll={keyNodeListTable.getScroll()}
        pagination={{
          className: 'mnc-pagination',
          current: pageNum,
          pageSize,
          total,
          onChange: (p, s) => {
            const coords = setListPagination(p, s);
            void loadNodes({ pageNum: coords.page, pageSize: coords.pageSize });
          },
        }}
      />

      {/* 添加/编辑抽屉 */}
      <Drawer
        title={editingNode ? '编辑关键节点' : '添加关键节点'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={600}
        extra={
          <Space>
            <IpcButton variant="secondary" onClick={() => setDrawerOpen(false)}>取消</IpcButton>
            <IpcButton variant="primary" onClick={handleSave}>保存</IpcButton>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item label="节点编码">
            <Input
              disabled
              value={editingNode?.nodeCode || `KN${String(Math.max(...allNodes.map(n => n.id), 0) + 1).padStart(3, '0')}`}
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label="节点名称"
            rules={[{ required: true, message: '请输入节点名称' }]}
          >
            <Input placeholder="请输入节点名称" maxLength={200} />
          </Form.Item>
          <Form.Item name="description" label="节点说明">
            <Input.TextArea placeholder="请输入节点说明" maxLength={500} rows={3} />
          </Form.Item>
          <Form.Item name="alertEnabled" label="预警状态" valuePropName="checked">
            <IpcSwitch
              withText
              onChange={checked => setAlertEnabled(checked)}
            />
          </Form.Item>
          <Form.Item
            name="alertDays"
            label="预警天数"
            style={{ display: alertEnabled ? 'block' : 'none' }}
            rules={[
              { required: alertEnabled, message: '请输入预警天数' },
              { type: 'number', min: 1, message: '预警天数必须 ≥ 1', transform: (v: unknown) => Number(v) },
            ]}
          >
            <Input type="number" placeholder="3" min={1} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* 映射配置抽屉 */}
      <Drawer
        title={`映射配置 — ${mappingNodeName}`}
        open={mappingDrawerOpen}
        onClose={() => setMappingDrawerOpen(false)}
        width={860}
        extra={
          <Space>
            <IpcButton variant="secondary" onClick={() => setMappingDrawerOpen(false)}>取消</IpcButton>
            <IpcButton variant="primary" onClick={handleSaveMappings}>保存映射</IpcButton>
          </Space>
        }
      >
        <div className="mnc-mapping-section">
          <h4>🔗 IPD 流程节点</h4>
          <div className="mnc-mapping-fields">
            <div className="mnc-mapping-field">
              <label className="mnc-mapping-label">IPD 工作项 <span className="mnc-required">*</span></label>
              <TableSelect<IpdWorkItemRow>
                value={ipdWorkItemTypes}
                onChange={handleIpdWorkItemChange}
                options={ipdWorkItemRows}
                rowKey={row => row.key}
                columns={ipdWorkItemColumns}
                placeholder="请选择工作项"
                getTagLabel={row => row.workItemTypeName}
              />
            </div>
            <div className="mnc-mapping-field">
              <label className="mnc-mapping-label">IPD 模板名称 <span className="mnc-required">*</span></label>
              <TableSelect<IpdTemplateRow>
                value={ipdTemplates}
                onChange={handleIpdTemplateChange}
                options={ipdTemplateRows}
                rowKey={row => row.key}
                columns={ipdTemplateColumns}
                placeholder={ipdWorkItemTypes.length === 0 ? '请先选择工作项' : '请选择模板'}
                disabled={ipdWorkItemTypes.length === 0}
                getTagLabel={row => row.templateName}
              />
            </div>
            <div className="mnc-mapping-field">
              <label className="mnc-mapping-label">IPD 节点名称 <span className="mnc-required">*</span></label>
              <TableSelect<IpdStateRow>
                value={ipdStates}
                onChange={setIpdStates}
                options={ipdStateRows}
                rowKey={row => row.key}
                columns={ipdStateColumns}
                placeholder={ipdTemplates.length === 0 ? '请先选择模板' : '请选择节点'}
                disabled={ipdTemplates.length === 0}
                getTagLabel={row => row.stateName}
              />
            </div>
          </div>
        </div>

        <div className="mnc-mapping-section">
          <h4>🔗 IPC 流程节点</h4>
          <div className="mnc-mapping-fields">
            <div className="mnc-mapping-field">
              <label className="mnc-mapping-label">IPC 模板名称 <span className="mnc-required">*</span></label>
              <TableSelect<IpcTemplateRow>
                value={ipcTemplates}
                onChange={handleIpcTemplateChange}
                options={ipcTemplateRows}
                rowKey={row => row.key}
                columns={ipcTemplateColumns}
                placeholder="请选择模板"
                getTagLabel={row => row.templateName}
              />
            </div>
            <div className="mnc-mapping-field">
              <label className="mnc-mapping-label">IPC 节点名称 <span className="mnc-required">*</span></label>
              <TableSelect<IpcNodeRow>
                value={ipcNodes}
                onChange={setIpcNodes}
                options={ipcNodeRows}
                rowKey={row => row.key}
                columns={ipcNodeColumns}
                placeholder={ipcTemplates.length === 0 ? '请先选择模板' : '请选择节点'}
                disabled={ipcTemplates.length === 0}
                getTagLabel={row => row.nodeName}
              />
            </div>
          </div>
        </div>
      </Drawer>

      {/* 查看映射弹窗 */}
      <IpcModal
        title={viewMappingNode ? `节点映射关系 — ${viewMappingNode.nodeCode} ${viewMappingNode.name}` : '节点映射关系'}
        open={viewMappingOpen}
        onCancel={() => {
          setViewMappingOpen(false);
          setViewMappings([]);
          setViewMappingRows([]);
          setViewMappingFilter(emptyViewMappingFilter);
          setViewMappingFilterApplied(emptyViewMappingFilter);
        }}
        footer={null}
        width={1440}
        style={{ maxWidth: '96vw' }}
        className="mnc-view-mapping-modal"
      >
        {viewMappingsLoading ? (
          <div className="mnc-view-mapping-empty">加载中...</div>
        ) : viewMappings.length === 0 ? (
          <div className="mnc-view-mapping-empty">暂未配置映射关系</div>
        ) : (
          <div className="ipc-section-stack mnc-view-mapping-stack">
            <IpcFilterToolbar
              className="mnc-view-mapping-filters"
              onReset={resetViewMappingFiltersToDefault}
              onSubmit={handleViewMappingSearch}
              chipItems={viewMappingAppliedChips}
              onRemoveChip={removeViewMappingAppliedChip}
              onClearChips={clearAllViewMappingFilters}
            >
              <Input
                placeholder="关键节点"
                value={viewMappingFilter.keyNode}
                onChange={e => setViewMappingFilter(prev => ({ ...prev, keyNode: e.target.value }))}
                onPressEnter={handleViewMappingSearch}
                allowClear
                className="ipc-filter-bar__field"
              />
              <Input
                placeholder="IPD 工作项"
                value={viewMappingFilter.ipdWorkItem}
                onChange={e => setViewMappingFilter(prev => ({ ...prev, ipdWorkItem: e.target.value }))}
                onPressEnter={handleViewMappingSearch}
                allowClear
                className="ipc-filter-bar__field"
              />
              <Input
                placeholder="IPD 模板"
                value={viewMappingFilter.ipdTemplate}
                onChange={e => setViewMappingFilter(prev => ({ ...prev, ipdTemplate: e.target.value }))}
                onPressEnter={handleViewMappingSearch}
                allowClear
                className="ipc-filter-bar__field"
              />
              <Input
                placeholder="IPD 节点"
                value={viewMappingFilter.ipdNode}
                onChange={e => setViewMappingFilter(prev => ({ ...prev, ipdNode: e.target.value }))}
                onPressEnter={handleViewMappingSearch}
                allowClear
                className="ipc-filter-bar__field"
              />
              <Input
                placeholder="IPC 模板"
                value={viewMappingFilter.ipcTemplate}
                onChange={e => setViewMappingFilter(prev => ({ ...prev, ipcTemplate: e.target.value }))}
                onPressEnter={handleViewMappingSearch}
                allowClear
                className="ipc-filter-bar__field"
              />
              <Input
                placeholder="IPC 节点"
                value={viewMappingFilter.ipcNode}
                onChange={e => setViewMappingFilter(prev => ({ ...prev, ipcNode: e.target.value }))}
                onPressEnter={handleViewMappingSearch}
                allowClear
                className="ipc-filter-bar__field"
              />
            </IpcFilterToolbar>

            <IpcTable<ViewMappingRow>
              panelClassName="mnc-view-mapping-table-panel"
              scrollClassName="mnc-view-mapping-table-scroll"
              dataSource={filteredViewMappingRows}
              rowKey="key"
              pagination={false}
              size="small"
              columns={viewMappingColumnsResizable.columns}
              components={viewMappingColumnsResizable.components}
              tableLayout={viewMappingColumnsResizable.tableLayout}
              className={viewMappingColumnsResizable.tableClassName}
              scroll={viewMappingColumnsResizable.getScroll({ y: 480 })}
              emptyTitle="无匹配映射"
              emptyDescription="尝试调整筛选条件"
              compact
            />
          </div>
        )}
      </IpcModal>

      {/* 删除确认 */}
      <IpcModal
        title="删除确认"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        footer={(
          <div className="ipc-modal__footer-actions">
            <IpcButton variant="link" onClick={() => setDeleteTarget(null)}>
              取消
            </IpcButton>
            <IpcButton variant="destructive" onClick={handleDelete}>
              确认
            </IpcButton>
          </div>
        )}
      >
        <p>确定删除节点「{deleteTarget?.name}」？删除后不可恢复。</p>
      </IpcModal>
    </div>
  );
};

export default MonitorNodeConfig;
